/**
 * LegalPageLayout.tsx — Shared wrapper for all public legal / docs pages.
 *
 * Used by PrivacyPolicy, TermsOfService, CookiePolicy, ApiDocs, and FAQ.
 * Handles:
 *   - Font loading + title
 *   - Sticky nav with logo + page label
 *   - Page title + cyan underline bar + "Last updated" line
 *   - Consistent long-form typography
 *   - Minimal footer
 *
 * Each consuming page just passes title, optional lastUpdated, and children.
 */

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const C = {
  bgBase: '#080C18',
  bgPanel: '#0B0F1F',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  textQuaternary: '#475569',
  cyan: '#00D4FF',
  cyanLight: '#7DE5F5',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
};

const F = { ui: "'DM Sans', -apple-system, system-ui, sans-serif" };

export { C, F };

/**
 * Wraps a legal/docs page with nav, styled title, and footer.
 * Content is passed as children — pages are free to render <Section>s inside.
 */
export function LegalPageLayout({
  pageTitle, browserTitle, lastUpdated, children, maxWidth = 800,
}: {
  pageTitle: string;
  browserTitle?: string;
  lastUpdated?: string;
  children: React.ReactNode;
  maxWidth?: number;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${browserTitle ?? pageTitle} | Meridian Registry`;
    if (!document.querySelector('link[href*="DM+Sans"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
      document.head.appendChild(link);
    }
    return () => { document.title = 'Meridian Registry'; };
  }, [pageTitle, browserTitle]);

  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      fontFamily: F.ui,
      minHeight: '100vh',
      position: 'relative',
      WebkitFontSmoothing: 'antialiased',
    }}>
      {/* Ambient glow */}
      <div aria-hidden style={{
        position: 'fixed',
        top: -300, right: -400,
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(8, 12, 24, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${C.borderSubtle}`,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '14px 32px',
          display: 'flex', alignItems: 'center',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, padding: 0,
            }}>
            <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 28 }} />
            <span style={{
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.22em',
              color: C.textPrimary, textTransform: 'uppercase',
            }}>Meridian</span>
          </button>
          <span style={{
            marginLeft: 16, paddingLeft: 16,
            borderLeft: `1px solid ${C.borderDefault}`,
            fontSize: 13, color: C.textSecondary,
          }}>{pageTitle}</span>
        </div>
      </nav>

      {/* Main — doc flow */}
      <main style={{
        position: 'relative', zIndex: 1,
        maxWidth, margin: '0 auto',
        padding: '60px 32px 100px',
      }}>
        <h1 style={{
          fontSize: 40, fontWeight: 600,
          letterSpacing: '-0.03em',
          color: C.textPrimary,
          marginBottom: 12,
          margin: '0 0 12px 0',
        }}>{pageTitle}</h1>

        {/* Cyan glow bar */}
        <div aria-hidden style={{
          height: 3, width: 56,
          background: C.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${C.cyanGlow}`,
          marginBottom: 16,
        }} />

        {lastUpdated && (
          <p style={{
            fontSize: 12, fontWeight: 600,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: C.textTertiary,
            marginBottom: 48,
            margin: '0 0 48px 0',
          }}>Last updated · {lastUpdated}</p>
        )}

        {children}
      </main>

      {/* Minimal footer */}
      <footer style={{
        borderTop: `1px solid ${C.borderSubtle}`,
        padding: '28px 32px',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'flex', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 16,
          fontSize: 12, color: C.textQuaternary,
        }}>
          <div>© {new Date().getFullYear()} Meridian Registry</div>
          <div style={{ display: 'flex', gap: 24 }}>
            <Link to="/privacy" style={{ color: C.textQuaternary, textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ color: C.textQuaternary, textDecoration: 'none' }}>Terms</Link>
            <Link to="/cookies" style={{ color: C.textQuaternary, textDecoration: 'none' }}>Cookies</Link>
            <a href="mailto:admin@meridianregistry.com" style={{ color: C.textQuaternary, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Section header + body. Default typography for long-form legal text. */
export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{
        fontSize: 20, fontWeight: 600,
        color: C.textPrimary,
        marginBottom: 14,
        letterSpacing: '-0.015em',
        margin: '0 0 14px 0',
      }}>{title}</h2>
      <div style={{
        color: C.textSecondary,
        fontSize: 15, lineHeight: 1.75,
      }}>{children}</div>
    </section>
  );
}

/** Styled unordered list for legal bullet lists. Stronger labels in primary text. */
export function Bullets({ items }: { items: { label?: string; text: React.ReactNode }[] }) {
  return (
    <ul style={{
      paddingLeft: 20, lineHeight: 1.9,
      margin: '12px 0 0',
      listStyle: 'none',
    }}>
      {items.map((item, i) => (
        <li key={i} style={{ position: 'relative', paddingLeft: 20, marginBottom: 6 }}>
          <span aria-hidden style={{
            position: 'absolute', left: 0, top: 11,
            width: 6, height: 6, borderRadius: 1,
            background: C.cyan, opacity: 0.7,
          }} />
          {item.label && (
            <strong style={{ color: C.textPrimary, fontWeight: 500 }}>{item.label}</strong>
          )}
          {item.label && ' — '}
          {item.text}
        </li>
      ))}
    </ul>
  );
}

/** Inline cyan link. */
export function A({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href}
      style={{ color: C.cyanLight, textDecoration: 'none' }}
      onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
      onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
      {children}
    </a>
  );
}

/** Bold label inside prose. Use instead of raw <strong> for consistency. */
export function L({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: C.textPrimary, fontWeight: 500 }}>{children}</strong>;
}
